import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Check, ExternalLink, ShieldCheck } from "lucide-react";
import api from "@/lib/api";
import { TopBar } from "@/pages/Dashboard";

export default function Settings() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState({});
  const [apiKey, setApiKey] = useState("");
  const [sender, setSender] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings/email").then((r) => { setInfo(r.data); setSender(r.data.sender || ""); })
      .catch((e) => { if (e?.response?.status === 401) navigate("/login"); })
      .finally(() => setLoading(false));
  }, [navigate]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings/email", { api_key: apiKey || undefined, sender: sender || undefined });
      toast.success("Email settings saved");
      const r = await api.get("/settings/email"); setInfo(r.data); setApiKey("");
    } catch { toast.error("Could not save settings"); }
    finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen">
      <TopBar />
      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <h1 className="font-display text-3xl sm:text-4xl font-semibold">Settings</h1>
        <p className="mt-1 text-[hsl(var(--muted-foreground))]">Connect email so invitations & reminders send automatically.</p>

        <div className="mt-8 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-6">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 grid place-items-center rounded-xl bg-[hsl(35_55%_92%)] text-[hsl(var(--accent))]"><Mail size={20} /></div>
            <div>
              <h2 className="font-display text-xl font-semibold">Email delivery (Resend)</h2>
              <p className="text-sm text-[hsl(var(--muted-foreground))]">
                {loading ? "Loading…" : info.configured ? <span className="inline-flex items-center gap-1.5 text-[hsl(var(--success))] font-medium"><Check size={15} /> Connected {info.api_key_masked ? `(${info.api_key_masked})` : ""}</span> : "Not connected yet"}
              </p>
            </div>
          </div>

          <div className="mt-6 space-y-4">
            <div>
              <label className="text-sm font-medium">Resend API key</label>
              <input data-testid="resend-key-input" type="password" value={apiKey} onChange={(e) => setApiKey(e.target.value)} placeholder={info.configured ? "Enter a new key to replace" : "re_..."} className="mt-1.5 w-full h-12 rounded-xl px-4 bg-[hsl(var(--background))] border border-[hsl(var(--input))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ring))]" />
              <a href="https://resend.com/api-keys" target="_blank" rel="noreferrer" className="mt-1.5 inline-flex items-center gap-1 text-xs text-[hsl(var(--accent))] hover:underline">Get a key at resend.com <ExternalLink size={12} /></a>
            </div>
            <div>
              <label className="text-sm font-medium">Sender address</label>
              <input data-testid="sender-email-input" value={sender} onChange={(e) => setSender(e.target.value)} placeholder="GatherVoices <hello@gathervoices.io>" className="mt-1.5 w-full h-12 rounded-xl px-4 bg-[hsl(var(--background))] border border-[hsl(var(--input))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ring))]" />
              <p className="mt-1.5 text-xs text-[hsl(var(--muted-foreground))]">Emails deliver to anyone only after your sending domain is verified in Resend. Until then, Resend&apos;s default sender only delivers to your own verified inbox.</p>
            </div>
            <button data-testid="save-email-settings-button" disabled={saving} onClick={save} className="h-12 px-6 rounded-xl bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-semibold disabled:opacity-60">{saving ? "Saving…" : "Save"}</button>
          </div>
        </div>

        <div className="mt-4 flex items-start gap-2 text-xs text-[hsl(var(--muted-foreground))]"><ShieldCheck size={14} className="mt-0.5" /> Your key is stored privately for your account and used only to send your event emails.</div>
      </main>
    </div>
  );
}
